import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { History, User, Clock, FileEdit, Plus, Trash2, Upload, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const ACTION_CONFIG = {
  create: { label: 'Creat', icon: Plus, color: 'bg-green-100 text-green-700' },
  update: { label: 'Modificat', icon: FileEdit, color: 'bg-blue-100 text-blue-700' },
  delete: { label: 'Sters', icon: Trash2, color: 'bg-red-100 text-red-700' },
  import: { label: 'Importat', icon: Upload, color: 'bg-purple-100 text-purple-700' },
};

const FIELD_LABELS = {
  name: 'Nume', code: 'Cod', category: 'Categorie', category_name: 'Categorie',
  plant_id: 'Sediu', plant_name: 'Sediu', location_id: 'Locatie', location_name: 'Locatie',
  manufacturer: 'Producator', model: 'Model', serial_number: 'Serie',
  description: 'Descriere', status: 'Status', unit: 'Unitate masura',
  min_stock: 'Stoc minim', xrp_code: 'Cod XRP', purchase_currency: 'Valuta',
  xrp_subaccount: 'Subcont XRP', email: 'Email', phone_fixed: 'Telefon fix',
  phone_mobile: 'Telefon mobil', address: 'Adresa', locality: 'Localitate',
  county: 'Judet', country: 'Tara', cif: 'CIF', website: 'Website',
  contact_person: 'Persoana contact', notes: 'Note', is_active: 'Activ',
  category_id: 'Categorie', year_manufactured: 'An fabricatie',
  lot_required: 'Lot obligatoriu', score: 'Scor',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('ro-RO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatValue(val) {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'boolean') return val ? 'Da' : 'Nu';
  if (Array.isArray(val)) return val.length > 0 ? `${val.length} elemente` : '-';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function AuditEntry({ log }) {
  const [expanded, setExpanded] = useState(false);
  const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.update;
  const Icon = config.icon;
  const details = log.details || {};
  const detailKeys = Object.keys(details).filter(k => 
    !['id', 'created_at', '_id', 'is_active', 'total_stock'].includes(k)
  );

  return (
    <div className="relative pl-8 pb-6 last:pb-0" data-testid={`audit-entry-${log.id}`}>
      <div className="absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-sm z-10"
        style={{ backgroundColor: config.color.includes('green') ? '#dcfce7' : config.color.includes('blue') ? '#dbeafe' : config.color.includes('red') ? '#fee2e2' : '#f3e8ff' }}>
        <Icon className="w-3 h-3" />
      </div>
      <div className="absolute left-[11px] top-7 bottom-0 w-0.5 bg-slate-200 last:hidden" />
      
      <div className="bg-white border border-slate-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Badge className={`${config.color} text-xs font-medium`}>{config.label}</Badge>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <User className="w-3 h-3" /> {log.user_name || 'Sistem'}
            </span>
          </div>
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {formatDate(log.created_at)}
          </span>
        </div>
        
        {detailKeys.length > 0 && (
          <>
            <button onClick={() => setExpanded(!expanded)} 
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mt-1">
              <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              {detailKeys.length} {detailKeys.length === 1 ? 'camp modificat' : 'campuri modificate'}
            </button>
            {expanded && (
              <div className="mt-2 space-y-1">
                {detailKeys.map(key => (
                  <div key={key} className="flex items-start gap-2 text-xs">
                    <span className="text-slate-500 min-w-[120px] font-medium">
                      {FIELD_LABELS[key] || key}:
                    </span>
                    <span className="text-slate-700 break-all">{formatValue(details[key])}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function AuditLogDialog({ open, onOpenChange, entityType, entityId, entityName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const fetchLogs = useCallback(async (currentSkip = 0) => {
    if (!entityId || !entityType) return;
    setLoading(true);
    try {
      const res = await api.get(`/audit?entity_type=${entityType}&entity_id=${entityId}&limit=${limit}&skip=${currentSkip}`);
      if (currentSkip === 0) {
        setLogs(res.data.logs || []);
      } else {
        setLogs(prev => [...prev, ...(res.data.logs || [])]);
      }
      setTotal(res.data.total || 0);
    } catch {
      setLogs([]);
    }
    setLoading(false);
  }, [entityId, entityType]);

  useEffect(() => {
    if (open && entityId) {
      setSkip(0);
      fetchLogs(0);
    }
  }, [open, entityId, fetchLogs]);

  const loadMore = () => {
    const newSkip = skip + limit;
    setSkip(newSkip);
    fetchLogs(newSkip);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="audit-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Istoric Modificari
          </DialogTitle>
          {entityName && <p className="text-sm text-slate-500 mt-1">{entityName}</p>}
        </DialogHeader>
        <ScrollArea className="flex-1 pr-2" style={{ maxHeight: 'calc(85vh - 120px)' }}>
          {loading && logs.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>Nu exista inregistrari in istoric</p>
            </div>
          ) : (
            <div className="py-2">
              {logs.map(log => <AuditEntry key={log.id} log={log} />)}
              {logs.length < total && (
                <div className="text-center mt-4">
                  <Button variant="outline" size="sm" onClick={loadMore} disabled={loading}>
                    {loading ? 'Se incarca...' : `Mai multe (${total - logs.length} ramase)`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export function AuditLogTimeline({ entityType, entityId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!entityId || !entityType) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/audit?entity_type=${entityType}&entity_id=${entityId}&limit=50`);
        setLogs(res.data.logs || []);
        setTotal(res.data.total || 0);
      } catch {
        setLogs([]);
      }
      setLoading(false);
    };
    fetch();
  }, [entityId, entityType]);

  const visibleLogs = showAll ? logs : logs.slice(0, 5);

  if (loading) return <div className="text-center py-4 text-slate-400">Se incarca istoricul...</div>;
  if (logs.length === 0) return <div className="text-center py-4 text-slate-400 text-sm">Nu exista inregistrari</div>;

  return (
    <div data-testid="audit-log-timeline">
      {visibleLogs.map(log => <AuditEntry key={log.id} log={log} />)}
      {!showAll && logs.length > 5 && (
        <button onClick={() => setShowAll(true)} 
          className="text-sm text-blue-600 hover:text-blue-800 mt-2 flex items-center gap-1">
          <ChevronDown className="w-4 h-4" /> Arata toate ({total} total)
        </button>
      )}
    </div>
  );
}

export default AuditLogDialog;
